import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useObservable, useObservableState } from 'observable-hooks';
import { map } from 'rxjs';
import { useQuery } from '@apollo/client';
import { BaseDataSet } from '../../../graph/dataset';
import { convertDistanceAndFloat, fixFloat, getDataType } from '../../../graph/data-types';
import { DATA_TYPE } from '../../../graph/enums';
import { namingConventions$, applyNamingConvention } from '../../../utils/naming-conventions';
import { NewPageTable } from '../../utils';
import { switchHead } from '../../../utils/rxjs';
import { highestVtopQuery, highestVtopPerDayQuery } from '../../../graph/queries';
import { getClient } from '../../../utils/apollo-client';
import { formatDatetimeTz } from '../../../utils/formatting';
import { useProfile } from '../../../profile';

function getTitleForType(datatype, profile) {
    const dataType = getDataType(datatype);
    return `${applyNamingConvention(dataType.title)} (${dataType.getUnit(profile)})`;
}

function ReadingsTable({ title, tableData }) {
    const namingConventions = useObservableState(namingConventions$);
    const profile = useProfile();

    const header = useMemo(
        () => [
            gettext('Date/Time'),
            gettext('AXIS'),
            getTitleForType(DATA_TYPE.FDOM, profile),
            getTitleForType(DATA_TYPE.VTOP, profile),
            gettext('READING_PERCENTAGE'),
        ],
        /*
         Disabling `exhaustive-deps` because in this case we know better than the
         linter. We want the header to update when the namingConventions change, but
         we are not really interested in the naming conventions themselves. So we
         use it in the dependency array but do not use it in the memo itself. The
         linter thinks we forgot to clean up something, but this is by design.
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [namingConventions, profile]
    );

    return <NewPageTable {...{ title, header, tableData }} />;
}

ReadingsTable.propTypes = {
    title: PropTypes.string.isRequired,
    tableData: PropTypes.array.isRequired,
};

function useDataQuery(dataSet, setReady, query, queryReturnName) {
    const shouldQuery$ = useObservable(
        (inputs$) =>
            inputs$.pipe(
                switchHead(),
                map((dataType) => dataType === DATA_TYPE.VTOP)
            ),
        [dataSet.dataType$]
    );
    const shouldQuery = useObservableState(shouldQuery$, null);

    const { data, loading } = useQuery(query, {
        client: getClient(),
        variables: {
            measuringPointId: Number(dataSet.sensor),
            startTime: dataSet.dateRange.min,
            endTime: dataSet.dateRange.max,
        },
        skip: !shouldQuery,
    });

    useEffect(() => {
        // Only respond if shouldQuery has been set by the shouldQuery$.
        if (!loading && shouldQuery !== null) {
            setReady();
        }
    }, [loading, setReady, shouldQuery]);

    return data?.[queryReturnName] || {};
}

function getRowData(timezone, profile, timestamp, axis, fdom, vtop, percentage) {
    return [
        formatDatetimeTz(timezone, timestamp),
        axis,
        fdom !== null ? convertDistanceAndFloat(DATA_TYPE.FDOM, fdom, profile) : '',
        vtop !== null ? convertDistanceAndFloat(DATA_TYPE.VTOP, vtop, profile) : '',
        // Use FDOM's datatype to round to one decimal place.
        percentage !== null ? fixFloat(DATA_TYPE.FDOM, percentage) : '',
    ];
}

export function HighestReadingTable({ dataSet, setReady }) {
    const profile = useProfile();
    const data = useDataQuery(dataSet, setReady, highestVtopQuery, 'highestVtopEntries');
    const timezone = useObservableState(dataSet.timezone$);
    const entries = data?.entries || [];
    if (entries.length === 0) return null;

    const axes = ['x', 'y', 'z'];
    if (data?.vectorEnabled) {
        axes.push('vector');
    }

    const tableData = entries.flatMap((tableRow) => [
        ...axes.map((axis) =>
            getRowData(
                timezone,
                profile,
                tableRow.timestamp,
                axis,
                tableRow.fdom[axis],
                tableRow.vtop[axis],
                tableRow.percentage[axis]
            )
        ),
    ]);

    const title = gettext('HIGHEST_READINGS_TABLE_TITLE');

    return <ReadingsTable {...{ title, tableData }} />;
}

HighestReadingTable.propTypes = {
    dataSet: PropTypes.instanceOf(BaseDataSet).isRequired,
    setReady: PropTypes.func.isRequired,
};

export function HighestAxisReadingTable({ dataSet, setReady }) {
    const profile = useProfile();
    const entries =
        useDataQuery(dataSet, setReady, highestVtopPerDayQuery, 'highestVtopPerDay')?.entries || [];
    const timezone = useObservableState(dataSet.timezone$);
    if (entries.length === 0) return null;

    const tableData = entries.map((tableRow) =>
        getRowData(
            timezone,
            profile,
            tableRow.timestamp,
            tableRow.axis,
            tableRow.fdom,
            tableRow.vtop,
            tableRow.percentage
        )
    );

    const title = gettext('HIGHEST_AXIS_READINGS_TABLE_TITLE');

    return <ReadingsTable {...{ title, tableData }} />;
}

HighestAxisReadingTable.propTypes = {
    dataSet: PropTypes.instanceOf(BaseDataSet).isRequired,
    setReady: PropTypes.func.isRequired,
};
